import {
  IProductCategory,
  IProductSlotPrice,
} from "@/types/db/product-category";
import { dexieDB } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import { recordOperationDuration } from "@/utils/vue-helpers/common";
import { getLanguage } from "@/utils/helpers/lang";

// Categories stored only for their team settings have no POS price and are not for sale (L3-8360)
const hasPOSPrice = (pc: IProductCategory) => pc.prices.length > 0;

/**
 * Options for querying product categories by ticket group
 */
export interface IProductCategoryQueryOptions {
  /** Filter by ticket group ID (uses multi-entry index) - required */
  ticketGroupId: number;

  /** Number of records to skip (default: 0) */
  skip?: number;

  /** Maximum number of records to return (default: 10000) */
  limit?: number;

  /** Whether to sort results by locale-aware name (default: false) */
  sort?: boolean;

  /** Whether to exclude certain types of categories */
  excludeTypes?: string[];
}

/**
 * Query product categories by ticket group
 * Uses multi-entry index on ticketGroups for efficient lookup
 * Returns results sorted by locale-aware name
 *
 * Type filtering should be done at component level
 *
 * @param options - Query options with required ticketGroupId
 * @returns Array of product categories matching the criteria
 *
 * @example
 * // Get paginated PCs for a ticket group
 * const pcs = await queryProductCategoriesByTicketGroup({
 *   ticketGroupId: 5,
 *   skip: 0,
 *   limit: 25,
 * });
 */
export async function queryProductCategoriesByTicketGroup(
  options: IProductCategoryQueryOptions
): Promise<IProductCategory[]> {
  const {
    ticketGroupId,
    skip = 0,
    limit,
    sort = false,
    excludeTypes = [],
  } = options;

  const t1 = performance.now();

  try {
    // Use multi-entry index on ticketGroups
    let results = await dexieDB.productCategories
      .where("ticketGroups")
      .equals(ticketGroupId)
      .filter(hasPOSPrice)
      .toArray();

    // Exclude certain types if specified
    if (excludeTypes.length > 0) {
      results = results.filter((pc) => !excludeTypes.includes(pc.type));
    }

    // Sort by locale-aware name if requested (must be in-memory due to dynamic locale)
    if (sort) {
      const locale = getLanguage();
      results.sort((a, b) => {
        const aName = a.nameLocale?.[locale] || a.name;
        const bName = b.nameLocale?.[locale] || b.name;
        return aName.localeCompare(bName, locale);
      });
    }

    // Apply pagination after filtering
    if (skip > 0) {
      results = results.slice(skip);
    }
    if (limit !== undefined) {
      results = results.slice(0, limit);
    }

    const duration = performance.now() - t1;
    recordOperationDuration(
      "dexie.queryProductCategoriesByTicketGroup",
      duration
    );

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.queryProductCategoriesByTicketGroup",
      { params: options, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get product categories by IDs
 * Uses direct primary key lookup - O(1) per ID
 *
 * @param ids - Array of product category IDs
 * @returns Array of product categories (order not guaranteed)
 */
export async function getProductCategoriesByIds(
  ids: number[]
): Promise<IProductCategory[]> {
  if (!ids || ids.length === 0) {
    return [];
  }

  const t1 = performance.now();

  try {
    const results = await dexieDB.productCategories
      .where("id")
      .anyOf(ids)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getProductCategoriesByIds", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoriesByIds",
      { params: { ids }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get a single product category by ID
 * Uses direct primary key lookup - O(1)
 *
 * @param id - Product category ID
 * @returns The product category or undefined if not found
 */
export async function getProductCategoryById(
  id: number
): Promise<IProductCategory | undefined> {
  try {
    return await dexieDB.productCategories.get(id);
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoryById",
      { params: { id }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Find a product category by barcode
 * Uses multi-entry index on barcodes array - O(log n)
 *
 * @param barcode - The barcode to search for
 * @returns The product category containing this barcode, or undefined
 */
export async function getProductCategoryByBarcode(
  barcode: string
): Promise<IProductCategory | undefined> {
  const t1 = performance.now();

  try {
    const pc = await dexieDB.productCategories
      .where("barcodes")
      .equals(barcode)
      .first();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getProductCategoryByBarcode", duration);

    return pc;
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoryByBarcode",
      { params: { barcode }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get product categories by their UUIDs.
 * The uuid field is not indexed, so this scans the table (same pattern as
 * getProductCategoriesByPriceUuids).
 *
 * @param uuids - Array of product category UUIDs
 * @returns Array of matching product categories
 */
export async function getProductCategoriesByUuids(
  uuids: string[]
): Promise<IProductCategory[]> {
  if (!uuids || uuids.length === 0) {
    return [];
  }

  const uuidSet = new Set(uuids);

  return dexieDB.productCategories
    .filter((pc) => uuidSet.has(pc.uuid))
    .toArray();
}

/**
 * Get product categories that contain any of the given price UUIDs.
 *
 * @param priceUUIDs - Array of price UUIDs to match
 * @returns Array of matching product categories (with all their prices)
 */
export async function getProductCategoriesByPriceUuids(
  priceUUIDs: string[]
): Promise<IProductCategory[]> {
  if (!priceUUIDs || priceUUIDs.length === 0) {
    return [];
  }

  const uuidSet = new Set(priceUUIDs);

  return dexieDB.productCategories
    .filter((pc) => pc.prices.some((price) => uuidSet.has(price.uuid)))
    .toArray();
}

/**
 * Get product categories that contain any of the given price UUIDs,
 * with only the matching prices kept in each result.
 *
 * @param priceUUIDs - Array of price UUIDs to match
 * @returns Product categories with their prices array filtered to only the requested UUIDs
 */
export async function getProductCategoriesWithFilteredPrices(
  priceUUIDs: string[]
): Promise<IProductCategory[]> {
  const pcs = await getProductCategoriesByPriceUuids(priceUUIDs);
  if (pcs.length === 0) return [];

  const uuidSet = new Set(priceUUIDs);
  return pcs.map((pc) => ({
    ...pc,
    prices: pc.prices.filter((price) => uuidSet.has(price.uuid)),
  }));
}

// ============================================================================
// Seating Plan Helper Functions
// ============================================================================

/**
 * Get product categories by seating parent ID
 * Uses index on seatingParentPSCId for efficient lookup
 *
 * @param seatingParentPSCId - The parent seating plan PC ID
 * @returns Array of child product categories
 */
export async function getProductCategoriesBySeatingParent(
  seatingParentPSCId: number
): Promise<IProductCategory[]> {
  const t1 = performance.now();

  try {
    const results = await dexieDB.productCategories
      .where("seatingParentPSCId")
      .equals(seatingParentPSCId)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration(
      "dexie.getProductCategoriesBySeatingParent",
      duration
    );

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoriesBySeatingParent",
      { params: { seatingParentPSCId }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get all product categories that have a seating parent
 * Uses index on seatingParentPSCId for efficient lookup (avoids full table scan)
 *
 * @returns Array of child product categories that reference a seating parent
 */
export async function getAllSeatingChildPCs(): Promise<IProductCategory[]> {
  const t1 = performance.now();

  try {
    const results = await dexieDB.productCategories
      .where("seatingParentPSCId")
      .above(0)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getAllSeatingChildPCs", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getAllSeatingChildPCs",
      { error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get non-recurring seating plan parent product categories
 *
 * @returns Array of non-recurring seating plan product categories
 */
export async function getNonRecurringSeatingPCs(): Promise<IProductCategory[]> {
  const t1 = performance.now();

  try {
    const results = await dexieDB.productCategories
      .where("type")
      .equals("Seated")
      .filter((pc) => !pc.isReccuringSeating)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getNonRecurringSeatingPCs", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getNonRecurringSeatingPCs",
      { error },
      LogLevel.Error
    );
    throw error;
  }
}

// ============================================================================
// Bulk Operations
// ============================================================================

/**
 * Bulk insert or update product categories
 * Uses bulkPut which inserts new records or updates existing ones by primary key
 *
 * @param pcs - Array of product categories to insert/update
 */
export async function bulkUpsertProductCategories(
  pcs: IProductCategory[]
): Promise<void> {
  if (!pcs || pcs.length === 0) {
    return;
  }

  const t1 = performance.now();

  try {
    await dexieDB.productCategories.bulkPut(pcs);

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.bulkUpsertProductCategories: ${
        pcs.length
      } PCs in ${duration.toFixed(2)}ms`
    );
    recordOperationDuration("dexie.bulkUpsertProductCategories", duration);
  } catch (error) {
    LogService.info(
      "Error in dexie.bulkUpsertProductCategories",
      { params: { count: pcs.length }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Clear all product categories from the database
 */
export async function clearProductCategoriesCollection(): Promise<void> {
  const t1 = performance.now();

  try {
    await dexieDB.productCategories.clear();

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.clearProductCategoriesCollection: Cleared in ${duration.toFixed(
        2
      )}ms`
    );
    recordOperationDuration("dexie.clearProductCategoriesCollection", duration);
  } catch (error) {
    LogService.info(
      "Error in dexie.clearProductCategoriesCollection",
      { error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Search product categories by name using case-insensitive substring matching
 * Searches both `name` and `nameLocale` (current locale) fields
 *
 * @param searchName - Substring to search for in product names (case-insensitive)
 * @param skip - Number of records to skip (default: 0)
 * @param limit - Maximum number of records to return (default: 1000)
 * @returns Array of matching product categories
 */
export async function searchProductCategories(
  searchName?: string,
  skip = 0,
  limit = 1000
): Promise<IProductCategory[]> {
  const t1 = performance.now();

  try {
    const search = searchName?.trim().toLowerCase();
    const locale = getLanguage();

    let results: IProductCategory[];

    if (!search) {
      // No search term: return paginated results directly
      results = await dexieDB.productCategories
        .filter(hasPOSPrice)
        .offset(skip)
        .limit(limit)
        .toArray();
    } else {
      // Substring search on name, nameLocale, barcodes, and SKUs
      results = await dexieDB.productCategories
        .filter((pc) => {
          if (!hasPOSPrice(pc)) {
            return false;
          }
          const name = pc.name?.toLowerCase() || "";
          const localeName = pc.nameLocale?.[locale]?.toLowerCase() || "";
          const matchesBarcodes =
            pc.barcodes?.some((b) => b.toLowerCase().includes(search)) ?? false;
          const matchesSkus =
            pc.skus?.some((s) => s.toLowerCase().includes(search)) ?? false;
          return (
            name.includes(search) ||
            localeName.includes(search) ||
            matchesBarcodes ||
            matchesSkus
          );
        })
        .offset(skip)
        .limit(limit)
        .toArray();
    }

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.searchProductCategories", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.searchProductCategories",
      { params: { searchName, skip, limit }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get a product category by price ID
 * Uses multi-entry index on priceIds for efficient lookup - O(log n)
 *
 * @param priceId - The price ID to search for
 * @returns The product category containing this price, or undefined
 */
export async function getProductCategoryByPriceId(
  priceId: number
): Promise<IProductCategory | undefined> {
  const t1 = performance.now();

  try {
    const result = await dexieDB.productCategories
      .where("priceIds")
      .equals(priceId)
      .first();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getProductCategoryByPriceId", duration);

    return result;
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoryByPriceId",
      { params: { priceId }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get a single price by its UUID
 * Performs a full table scan since prices are nested and there is no UUID index.
 *
 * @param priceUuid - The price UUID to search for
 * @returns The matching price, or undefined if not found
 */
export async function getPriceByUuid(
  priceUuid: string
): Promise<IProductSlotPrice | undefined> {
  const t1 = performance.now();

  try {
    const pc = await dexieDB.productCategories
      .filter((pc) => pc.prices.some((p) => p.uuid === priceUuid))
      .first();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getPriceByUuid", duration);

    return pc?.prices.find((p) => p.uuid === priceUuid);
  } catch (error) {
    LogService.info(
      "Error in dexie.getPriceByUuid",
      { params: { priceUuid }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get product categories by multiple price IDs
 * Uses multi-entry index on priceIds for efficient lookup
 *
 * @param priceIds - Array of price IDs to search for
 * @returns Array of product categories containing any of these prices
 */
export async function getProductCategoriesByPriceIds(
  priceIds: number[]
): Promise<IProductCategory[]> {
  if (!priceIds || priceIds.length === 0) {
    return [];
  }

  const t1 = performance.now();

  try {
    const results = await dexieDB.productCategories
      .where("priceIds")
      .anyOf(priceIds)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getProductCategoriesByPriceIds", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoriesByPriceIds",
      { params: { priceIds }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get product categories by stock IDs
 * Uses multi-entry index on stockIds for efficient lookup - O(log n) per stock ID
 *
 * @param stockIds - Array of stock IDs to search for
 * @returns Array of product categories containing any of these stock IDs
 */
export async function getProductCategoriesByStockIds(
  stockIds: number[]
): Promise<IProductCategory[]> {
  if (!stockIds || stockIds.length === 0) {
    return [];
  }

  const t1 = performance.now();

  try {
    // Use multi-entry index on stockIds for efficient lookup
    // Returns all PCs that have any of the provided stockIds
    const results = await dexieDB.productCategories
      .where("stockIds")
      .anyOf(stockIds)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getProductCategoriesByStockIds", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getProductCategoriesByStockIds",
      { params: { stockIds }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Delete product categories by IDs
 * Uses bulkDelete for efficient removal by primary key
 */
export async function deleteProductCategoriesByIds(
  ids: number[]
): Promise<void> {
  if (!ids || ids.length === 0) {
    return;
  }

  const t1 = performance.now();

  try {
    await dexieDB.productCategories.bulkDelete(ids);

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.deleteProductCategoriesByIds: Deleted ${
        ids.length
      } PCs in ${duration.toFixed(2)}ms`
    );
    recordOperationDuration("dexie.deleteProductCategoriesByIds", duration);
  } catch (error) {
    LogService.info(
      "Error in dexie.deleteProductCategoriesByIds",
      { params: { ids }, error },
      LogLevel.Error
    );
    throw error;
  }
}
